import { Primitive } from '@radix-ui/react-primitive';
import { type ComponentPropsWithoutRef, memo } from 'react';

import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const badge = tailwindVariants({
  base: 'inline-flex items-center justify-between gap-1 font-bold uppercase tracking-wide transition-colors',
  defaultVariants: {
    appearance: 'square',
    color: 'grey',
    size: 'small',
  },
  variants: {
    appearance: {
      pill: 'rounded-full',
      square: 'rounded-2',
    },
    color: {
      blue: 'bg-blue-8 text-blue-1',
      green: 'bg-green-4 text-green-11',
      grey: 'bg-grey-4 text-grey-10',
      'light-blue': 'bg-blue-2 text-blue-9',
      red: 'bg-red-10 text-red-1',
      teal: 'bg-teal-2 text-teal-10',
      yellow: 'bg-yellow-3 text-orange-11',
    },
    size: {
      medium: 'h-5 px-1.5 text-12',
      small: 'h-4 px-1.5 text-10',
    },
  },
});

type BadgeVariants = VariantProps<typeof badge>;

export type BadgeProps = Omit<ComponentPropsWithoutRef<'div'>, keyof BadgeVariants> & BadgeVariants;

export const Badge = memo(({ appearance, className, color = 'grey', size = 'small', ...restOfProps }: BadgeProps) => {
  return <Primitive.div className={badge({ appearance, className, color, size })} {...restOfProps} />;
});

Badge.displayName = 'Badge';
